import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AuthShell, GoogleButton } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSelect } from "@/components/LanguageSelect";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Signup() {
  const { register } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const nav = useNavigate();
  const ref = new URLSearchParams(window.location.search).get("ref") || "";
  const joinCode = new URLSearchParams(window.location.search).get("join") || "";
  const teamToken = new URLSearchParams(window.location.search).get("team") || "";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register({ ...form, language: lang, referral_code: ref || null });
      // whatever MiLu was told as a guest travels into the new account
      try {
        const said = JSON.parse(localStorage.getItem("chatmilu.milu.chat") || "[]");
        if (said.length > 1) {
          const { data } = await api.post("/wingman/handoff", {
            session_id: localStorage.getItem("chatmilu.milu.session") || "",
            lines: said.map((m) => `${m.role === "milu" ? "MiLu" : "Visitor"}: ${m.text}`),
          });
          if (data.brief) toast.success(`MiLu passed on your chat: ${data.brief}`);
        }
      } catch {
        /* the handoff is a nicety, never a blocker */
      }
      toast.success(
        ref ? "Account created — 1 free Premium month applied!" : "Account created — welcome to ChatMiLu"
      );
      nav(joinCode ? `/join/${joinCode}` : teamToken ? `/invite/team/${teamToken}` : "/chat");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="signup-page">
      <AuthShell title={t("signup")} subtitle="Free forever plan. No card required.">
        <div className="mt-6 space-y-4">
          {ref && (
            <p
              data-testid="referral-banner"
              className="rounded-2xl border border-brand/40 bg-brand/10 px-4 py-3 text-sm"
            >
              Invite code <b>{ref}</b> applied — you and your friend each get 1 free Premium month.
            </p>
          )}
          <div
            data-testid="signup-language-step"
            className="rounded-2xl border border-brand/30 bg-brand/5 p-4"
          >
            <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">
              First, your language
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              Everything — MiLu, your messages, the news — arrives in this one. You can change it any time.
            </p>
            <div className="mt-3">
              <LanguageSelect
                languages={languages}
                value={lang}
                onChange={setLang}
                testId="signup-language"
              />
            </div>
          </div>

          <GoogleButton testid="google-signup-button" />
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-ink-soft">
            <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name" data-testid="signup-name-input" required className="mt-1.5 min-h-[44px] rounded-xl"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="semail">Email</Label>
              <Input
                id="semail" data-testid="signup-email-input" type="email" required
                className="mt-1.5 min-h-[44px] rounded-xl"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="spassword">Password (min 6 characters)</Label>
              <Input
                id="spassword" data-testid="signup-password-input" type="password" required minLength={6}
                className="mt-1.5 min-h-[44px] rounded-xl"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {error && (
              <p data-testid="signup-error" className="rounded-xl bg-destructive/10 px-4 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              data-testid="signup-submit" type="submit" disabled={busy}
              className="min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
            >
              {busy ? "Creating account…" : t("signup")}
            </Button>
          </form>
          <p className="text-sm text-ink-soft">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login" className="font-semibold text-brand">
              {t("login")}
            </Link>
          </p>
          <p className="text-xs text-ink-soft">
            By continuing you agree to our GDPR-compliant privacy terms. You can export or delete your data any
            time from Settings.
          </p>
        </div>
      </AuthShell>
    </div>
  );
}
